import { useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { captureComparisonImage } from "../utils/captureComparisonImage";
import { uploadComparisonImageService } from "../services/FileUploadService";
import { useAxiosInstance } from "../services/Interceptor";
import { toast } from "react-toastify";
import {
  faEnvelope,
  faMessage,
  faShareAlt,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebook,
  faInstagram,
  faXTwitter,
} from "@fortawesome/free-brands-svg-icons";
import { SocialPostModal } from "./modal/socialPostModal";

const DEFAULT_POST_TEXT = {
  x: "Athlete comparison! ",
  facebook: "Check out this detailed athlete comparison! ",
  instagram: "Athlete comparison! ",
  email: "Hi,\n\nCheck out this athlete comparison:\n",
  sms: "Check out this athlete comparison:",
};

function getDefaultPostText(platform) {
  return DEFAULT_POST_TEXT[platform] || "Check out this athlete comparison!";
}

export function ShareComparisonButton({ comparisonRef }) {
  const apiInstance = useAxiosInstance();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPlatform, setModalPlatform] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [postText, setPostText] = useState("");

  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const menuRef = useRef(null);

  const handleOpenMenu = () => {
    setOpen((prev) => !prev);

    requestAnimationFrame(() => {
      menuRef.current?.focus();
    });
  };

  const handleGenerateAndOpenModal = async (platform) => {
    if (platform === "sms" && !isMobile) {
      toast.info("SMS is only available on mobile devices.");
      return;
    }

    setLoading(true);

    try {
      const dataUrl = await captureComparisonImage(comparisonRef);
      const uploadedImageUrl = await uploadComparisonImageService(
        apiInstance,
        dataUrl,
        "comparison.png",
      );

      setImageUrl(uploadedImageUrl);
      setPostText(getDefaultPostText(platform));
      setModalPlatform(platform);
      setModalOpen(true);
    } catch {
      toast.error("Error generating the image.");
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  return (
    <div className="relative inline-block mx-2">
      <button
        onClick={handleOpenMenu}
        className="bg-gray-300 text-black px-4 py-2 rounded-md flex items-center gap-2 shadow-sm hover:bg-blue-500 transition"
      >
        {loading ? "Generating..." : "SHARE"}
        <FontAwesomeIcon icon={faShareAlt} />
      </button>

      {open && !loading && (
        <div
          ref={menuRef}
          tabIndex={-1}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              setOpen(false);
            }
          }}
          className="absolute right-0 mt-2 bg-white shadow-lg rounded-md border w-52 z-50 outline-none"
        >
          <button
            onClick={() => handleGenerateAndOpenModal("x")}
            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 w-full text-left text-sm"
          >
            <FontAwesomeIcon icon={faXTwitter} />X (Twitter)
          </button>

          <button
            onClick={() => handleGenerateAndOpenModal("instagram")}
            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 w-full text-left text-sm"
          >
            <FontAwesomeIcon icon={faInstagram} />
            Instagram
          </button>

          <button
            onClick={() => handleGenerateAndOpenModal("facebook")}
            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 w-full text-left text-sm"
          >
            <FontAwesomeIcon icon={faFacebook} />
            Facebook
          </button>

          <button
            onClick={() => handleGenerateAndOpenModal("email")}
            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 w-full text-left text-sm"
          >
            <FontAwesomeIcon icon={faEnvelope} />
            Email
          </button>

          {isMobile && (
            <button
              onClick={() => handleGenerateAndOpenModal("sms")}
              className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 w-full text-left text-sm"
            >
              <FontAwesomeIcon icon={faMessage} />
              SMS
            </button>
          )}
        </div>
      )}

      {modalOpen && (
        <SocialPostModal
          platform={modalPlatform}
          imageUrl={imageUrl}
          postText={postText}
          setPostText={setPostText}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}
